/**
 * Localhost NFC bridge client.
 *
 * Connects to `ws://127.0.0.1:26565` (the `utilsync-nfc-bridge` Python
 * service running on the tech's laptop), passes the user's Keycloak JWT
 * for the bridge to verify against Keycloak JWKS, and exposes a tiny
 * Promise-based API the SPA pages call.
 *
 * Why localhost-only:
 *   The bridge binds 127.0.0.1 — never the LAN. The browser uses
 *   `ws://127.0.0.1:…` because we're connecting to the same machine
 *   the SPA is loaded in. The bridge enforces an Origin allowlist
 *   on the upgrade so other tabs (or any malicious site that ran a
 *   `new WebSocket('ws://127.0.0.1:26565')`) get rejected at the
 *   handshake.
 *
 * Graceful degradation:
 *   `getStatus()` resolves to `'online'` / `'offline'` so pages can
 *   show a "bridge offline" tooltip without breaking the existing
 *   OCR / manual-entry flows. NFC is purely additive.
 */

import { api } from '../api/client';

const BRIDGE_PORT = 26565;
const BRIDGE_WS_URL = `ws://127.0.0.1:${BRIDGE_PORT}`;

// Tap envelope — matches the Python bridge's MeterTap.to_dict() schema
// 1:1 (and the iOS helper's POST body shape, locked in the plan file).
export interface NfcMeterTap {
  type: 'meter_tap';
  transport: 'nfc-1581' | 'nfc-iphone-corenfc' | 'nfc-android-tagdispatch';
  timestamp: string;
  meter: {
    serial: string | null;
    manufacturer: string;     // "AXI" for Axioma
    device_type: 'water' | 'electricity' | 'unknown';
    chip_uid: string;
  };
  readings: {
    waterVolume_m3?: number;  // present when reading a water meter
  };
  alarms: string[];
  raw_response_hex: string;
  sub?: string;              // Keycloak subject — bridge stamps it on send
}

interface BridgeError {
  type: 'tap_error' | 'tap_timeout' | 'error';
  message?: string;
  code?: string;
}

export type NfcBridgeStatus = 'online' | 'offline' | 'unauthenticated';

/**
 * Probe whether the bridge is reachable and our token is accepted.
 * Resolves quickly — never blocks the UI for more than ~1.5s. Use to
 * decide whether to render the "Read meter (NFC)" button or the
 * "bridge offline" pill.
 */
export async function getBridgeStatus(timeoutMs = 1500): Promise<NfcBridgeStatus> {
  const token = api.getToken();
  if (!token) return 'unauthenticated';
  const url = `${BRIDGE_WS_URL}/?token=${encodeURIComponent(token)}`;
  return new Promise((resolve) => {
    let settled = false;
    const finish = (status: NfcBridgeStatus) => {
      if (settled) return;
      settled = true;
      resolve(status);
    };
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      finish('offline');
      return;
    }
    const timer = setTimeout(() => {
      try { ws.close(); } catch { /* ignore */ }
      finish('offline');
    }, timeoutMs);
    ws.addEventListener('message', (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === 'ready') {
          clearTimeout(timer);
          ws.close();
          finish('online');
        }
      } catch { /* ignore */ }
    });
    ws.addEventListener('close', (ev) => {
      clearTimeout(timer);
      if (!settled) {
        // 1008 = policy violation (origin / token rejection)
        finish(ev.code === 1008 ? 'unauthenticated' : 'offline');
      }
    });
    ws.addEventListener('error', () => {
      clearTimeout(timer);
      finish('offline');
    });
  });
}

/**
 * Trigger a tap. Resolves with the MeterTap envelope after the user
 * presents the meter, or rejects on timeout / bridge error. The bridge
 * itself enforces an 8s tap timeout — the wrapper here adds a couple
 * seconds for WS round-trip jitter so the inner timeout fires first.
 */
export async function tapMeter(
  opts: { signal?: AbortSignal; outerTimeoutMs?: number } = {},
): Promise<NfcMeterTap> {
  const token = api.getToken();
  if (!token) throw new Error('Not authenticated');
  const outerTimeoutMs = opts.outerTimeoutMs ?? 12_000;

  return new Promise<NfcMeterTap>((resolve, reject) => {
    const ws = new WebSocket(`${BRIDGE_WS_URL}/?token=${encodeURIComponent(token)}`);
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      try { ws.close(); } catch { /* ignore */ }
      reject(new Error('NFC bridge timeout — is the bridge running?'));
    }, outerTimeoutMs);

    const abortHandler = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { ws.close(); } catch { /* ignore */ }
      reject(new Error('Aborted'));
    };
    if (opts.signal) {
      if (opts.signal.aborted) {
        abortHandler();
        return;
      }
      opts.signal.addEventListener('abort', abortHandler, { once: true });
    }

    ws.addEventListener('open', () => {
      // The bridge sends `{type: "ready"}` first; we wait for that
      // before sending `tap` so we don't race the upgrade handshake.
    });

    ws.addEventListener('message', (ev) => {
      let msg: unknown;
      try { msg = JSON.parse(ev.data); } catch {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        try { ws.close(); } catch { /* ignore */ }
        reject(new Error('Bridge sent malformed JSON'));
        return;
      }
      const m = msg as { type?: string };
      if (m.type === 'ready') {
        ws.send(JSON.stringify({ action: 'tap' }));
        return;
      }
      if (m.type === 'meter_tap') {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        try { ws.close(); } catch { /* ignore */ }
        resolve(msg as NfcMeterTap);
        return;
      }
      if (m.type === 'tap_timeout') {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        try { ws.close(); } catch { /* ignore */ }
        reject(new Error('No meter detected on the reader within 8 seconds'));
        return;
      }
      if (m.type === 'tap_error' || m.type === 'error') {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        try { ws.close(); } catch { /* ignore */ }
        const err = msg as BridgeError;
        reject(new Error(err.message || err.code || 'NFC bridge error'));
        return;
      }
    });

    ws.addEventListener('close', () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(new Error('NFC bridge connection closed unexpectedly'));
    });

    ws.addEventListener('error', () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(new Error('NFC bridge connection failed'));
    });
  });
}

/**
 * Tap + audit. Combines `tapMeter()` with a follow-up POST to
 * `/api/v2/nfc-bridge/audit` so the server has a record of every tap
 * (chip UID, transport, raw bytes for replay). Audit failure does NOT
 * fail the tap — the SPA still gets to prefill from the local read.
 */
export async function tapAndAudit(opts?: {
  signal?: AbortSignal;
  meterSerialHint?: string;
}): Promise<NfcMeterTap> {
  const event = await tapMeter(opts);
  try {
    await api.auditNfcTap({
      transport: event.transport,
      chip_uid: event.meter.chip_uid,
      meter_serial: opts?.meterSerialHint ?? event.meter.serial ?? undefined,
      volume_m3: event.readings.waterVolume_m3,
      raw_response_hex: event.raw_response_hex,
    });
  } catch (err) {
    // Audit failures are non-fatal — log but don't break the form.
    console.warn('NFC tap audit POST failed:', err);
  }
  return event;
}
